/**
 * Shared search + sort helpers for Field Report tabs.
 *
 * Extracted after iter-120 flagged that the Approved tab had drifted
 * a private copy of the `filterAndSort` cmpDate/cmpName block. All
 * five tabs now share this single implementation so the two can't
 * fork again.
 *
 * Contract:
 *   sortReports(reports, sortBy, searchQuery, {
 *     getTitle,      // (r) => string
 *     getDate,       // (r) => string | undefined
 *     searchFields,  // (r) => string[] — additional fields to search
 *   })
 *
 * `getTitle` / `getDate` are injected instead of imported so this
 * module stays free of the FieldReports.js closure (dates + titles
 * are page-level concerns).
 */

export function sortReports(reports, sortBy, searchQuery, { getTitle, getDate, searchFields }) {
  const q = (searchQuery || '').trim().toLowerCase();
  let out = reports;

  if (q) {
    out = out.filter((r) => {
      const fields = searchFields ? searchFields(r) : [];
      const all = [getTitle(r), ...fields];
      return all.some((f) => (f || '').toString().toLowerCase().includes(q));
    });
  }

  const cmpDate = (a, b) =>
    (new Date(getDate(a) || 0).getTime() || 0) -
    (new Date(getDate(b) || 0).getTime() || 0);

  const cmpName = (a, b) =>
    getTitle(a).toString().toLowerCase()
      .localeCompare(getTitle(b).toString().toLowerCase());

  const sorted = [...out];
  switch (sortBy) {
    case 'date_asc':
      sorted.sort(cmpDate);
      break;
    case 'name_asc':
      sorted.sort(cmpName);
      break;
    case 'name_desc':
      sorted.sort((a, b) => -cmpName(a, b));
      break;
    case 'status':
      sorted.sort((a, b) => (a.status || '').localeCompare(b.status || ''));
      break;
    case 'date_desc':
    default:
      sorted.sort((a, b) => -cmpDate(a, b));
  }
  return sorted;
}

// Standard set of searchable text fields on a field-report doc.
// The Approved tab AND the per-type tabs both use exactly this list —
// factored out so a future added field lands in one place.
export const DEFAULT_SEARCH_FIELDS = (r) => [
  r.assigned_team,
  r.job_number,
  r.address,
  r.site_location,
  r.location,
  r.client_name,
  r.contact_name,
  r.notes,
];
